import { useRef, useCallback, useState } from 'react';
import shaka from 'shaka-player/dist/shaka-player.ui';
import { isAppleDevice, isPlayReadySupported, supportsWidevinePersistentLicenses } from '../utils/devices';
import { Playlist } from '../api';
import { MuxAnalyticsConfig } from '../types';
import initShakaPlayerMux from '@mux/mux-data-shakaplayer';
import { version } from '../../package.json';
import {
  PersistentSessionMetadata,
  storePersistentLicense,
  retrievePersistentLicense,
  clearAllPersistentLicenses
} from '../utils/licenseCache';

/**
 * Removes the query string (and hash) from a URL, dropping signed CDN tokens
 * before the URL is reported to analytics. Returns the input unchanged if it
 * cannot be parsed.
 */
const stripUrlQuery = (url: string): string => {
  if (!url) return url;
  const queryIndex = url.search(/[?#]/);
  return queryIndex === -1 ? url : url.slice(0, queryIndex);
};

/**
 * Wraps a Shaka player in a Proxy that scrubs signed CDN tokens from the two
 * URL sources Mux reads — getAssetUri() (video_source_url) and each
 * response.uri (request_url/request_hostname) — WITHOUT touching the real
 * player. The signature must stay on every URL Shaka uses; stripping the real
 * response.uri made Shaka refresh the live media playlist unsigned (HTTP 500).
 * Only the Mux-facing view is scrubbed:
 *  - getAssetUri() returns the de-tokenized load URL.
 *  - the networking engine's registerResponseFilter wraps Mux's filter so it
 *    receives a shallow clone of each response with a stripped uri, leaving the
 *    real response object (and Shaka's playback) untouched.
 */
const createMuxScrubbingPlayer = (player: any): any =>
  new Proxy(player, {
    get(target, prop) {
      if (prop === 'getAssetUri') {
        return () => stripUrlQuery(target.getAssetUri?.() ?? '');
      }
      if (prop === 'getNetworkingEngine') {
        return () => {
          const engine = target.getNetworkingEngine();
          if (!engine) return engine;
          return new Proxy(engine, {
            get(engineTarget, engineProp) {
              if (engineProp === 'registerResponseFilter') {
                return (filter: any) =>
                  engineTarget.registerResponseFilter(
                    (type: any, response: any, ...rest: any[]) =>
                      filter(
                        type,
                        { ...response, uri: stripUrlQuery(response?.uri) },
                        ...rest
                      )
                  );
              }
              const value = engineTarget[engineProp];
              return typeof value === 'function' ? value.bind(engineTarget) : value;
            },
          });
        };
      }
      // Return `constructor` unbound: Mux validates the player via
      // `player.constructor.version`, and a bound function loses the original's
      // static `version` property — which made initShakaPlayerMux reject the
      // proxy ("You must provide a valid shakaPlayer") and skip all analytics.
      const value = target[prop];
      if (typeof value !== 'function' || prop === 'constructor') return value;
      return value.bind(target);
    },
  });

/**
 * Manages the Shaka Player lifecycle, configuration, and low-level operations.
 * 
 * RESPONSIBILITIES:
 * ----------------
 * - Create/destroy Shaka Player instances with proper lifecycle coordination
 * - Configure DRM systems (Widevine, PlayReady, FairPlay) with platform-specific settings
 * - Handle persistent license caching and stale license recovery
 * - Setup network filters for authentication headers and FairPlay base64 decoding
 * - Error handling with automatic retry logic for DRM failures
 * - Mux Analytics integration for monitoring and debugging
 * 
 * IMPORTANT: Conditional Manifest Loading
 * ---------------------------------------
 * This hook skips automatic manifest loading when hasAds=true or hasSystem73=true.
 * This is critical because:
 * 
 * - System73 SDK must wrap the player BEFORE manifest loads to detect all ABR profiles
 * - Google IMA ads need to intercept loading to serve pre-roll ads
 * 
 * When skipped, the caller (typically usePlayerInitialization) must explicitly call
 * loadManifest() after the third-party integrations are ready.
 * 
 * RELATIONSHIP TO OTHER HOOKS:
 * ---------------------------
 * This hook provides the "HOW" - it knows how to configure and manage Shaka Player.
 * It does NOT control "WHEN" - that's handled by usePlayerInitialization, which
 * orchestrates the initialization sequence across all integrations.
 * 
 * EXPORTED METHODS:
 * ----------------
 * - initializePlayer(video): Creates and configures player, but may skip manifest load
 * - loadManifest(): Explicitly loads the manifest (called after System73/ads setup)
 * - destroyPlayer(): Cleans up player instance, listeners, and resources
 * 
 * REFS & STATE:
 * ------------
 * - playerRef: Reference to Shaka Player instance
 * - videoElementRef: Reference to HTMLVideoElement for retry functionality
 * - isRetrying: State flag to prevent multiple simultaneous retry attempts
 * - Multiple DRM-related refs for persistent license management and cleanup
 */

interface DRMConfig {
  clearKeys?: { [keyId: string]: string };
  servers?: { [keySystem: string]: string };
}

// Maps the CDN's X-Error-Code segment response header to the translation key
// shown on the player's blocking error screen. 940 (playback token expired,
// e.g. after a long pause) is handled separately by reloading the page so a
// fresh token is issued.
const SEGMENT_ERROR_MESSAGE_KEYS: Record<string, string> = {
  '965': 'ERROR_CODE_IP_CHANGED',
  '970': 'ERROR_CODE_VPN_DETECTED',
  '971': 'ERROR_CODE_ANONYMOUS_PROXY',
};

interface UseShakaPlayerProps {
  src: Playlist | string;
  shakaConfig?: any;
  drmConfig?: DRMConfig;
  onError?: (error: Error) => void;
  /** Fired when a segment response carries an X-Error-Code that blocks
   * playback (IP change, VPN, anonymous proxy). Receives the translation key
   * of the message to display. */
  onPlaybackBlocked?: (messageKey: string) => void;
  /** Fired when playback cannot continue and no more specific handling
   * applies (blocked segments go through onPlaybackBlocked instead), so the
   * player can show a default error screen rather than load forever. */
  onFatalError?: () => void;
  onPlayerReady?: (player: any) => void;
  muxConfig?: MuxAnalyticsConfig;
  onMuxReady?: () => void;
  onMuxDataUpdate?: (data: any) => void;
  publicKey?: string;
  mottoToken?: string;
  hasAds?: boolean;
  hasSystem73?: boolean;
  apiToken?: string;
}

// Helper to normalize src to a Playlist-like object
const normalizeSource = (src: Playlist | string): Playlist => {
  if (typeof src === 'string') {
    return {
      id: '',
      url: src,
      format: 'auto',
      drm: {}
    } as Playlist;
  }
  return src;
};

export const useShakaPlayer = ({
  src,
  shakaConfig,
  drmConfig,
  onError,
  onPlaybackBlocked,
  onFatalError,
  onPlayerReady,
  muxConfig,
  onMuxReady,
  onMuxDataUpdate,
  publicKey,
  mottoToken,
  hasAds = false,
  hasSystem73 = false,
  apiToken
}: UseShakaPlayerProps) => {
  const playerRef = useRef<any>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const videoElementRef = useRef<HTMLVideoElement | null>(null);

  // Monitor returned by initShakaPlayerMux. Needed for loadErrorHandler:
  // errors thrown by player.load() reject the promise and never fire Shaka's
  // 'error' event, so automaticErrorTracking cannot see them.
  const muxMonitorRef = useRef<any>(null);

  // Lifecycle coordination to prevent init/destroy races
  const destroyInProgressRef = useRef<Promise<void> | null>(null);
  const isDestroyingRef = useRef<boolean>(false);
  const initSequenceRef = useRef<number>(0);
  const activeInitIdRef = useRef<number | null>(null);

  /* DRM related state */
  const waitingForKeyTimerRef = useRef<number | null>(null);
  const waitingForKeyHandlerRef = useRef<((e: Event) => void) | null>(null);
  const playbackResumedHandlerRef = useRef<((e: Event) => void) | null>(null);
  const usingPersistentLicenseRef = useRef<boolean>(false);
  const storedPersistentThisLoadRef = useRef<boolean>(false);
  const drmExpirationHandlerRef = useRef<((e: Event) => void) | null>(null);

  // Ensures an X-Error-Code on a segment response is acted on only once per
  // load: segment requests keep coming (and retrying) while blocked, so
  // without this guard we would reload the page or re-raise the error screen
  // repeatedly.
  const segmentErrorHandledRef = useRef<boolean>(false);

  // Helper function to determine the correct manifest URL based on DRM support
  const getManifestUrl = useCallback(() => {
    const playlistSrc = normalizeSource(src);
    let manifestUrl = playlistSrc.url;
    const isDRM = Boolean(playlistSrc.drm?.widevine || playlistSrc.drm?.fairplay || playlistSrc.drm?.playready);

    if (isDRM) {
      const isPlayReady = isPlayReadySupported();
      if(isAppleDevice() && playlistSrc.drm?.fairplay?.certificateUrl) {
        manifestUrl = playlistSrc.drm.fairplay.playlistUrl;
      } else if(isPlayReady && playlistSrc.drm?.playready?.licenseUrl) {
        manifestUrl = playlistSrc.drm.playready.playlistUrl;
      } else {
        manifestUrl = playlistSrc.drm?.widevine?.playlistUrl || "";
      }
    }

    return manifestUrl;
  }, [src]);

  // Acts on the X-Error-Code header of a segment response. Returns true when
  // the code was recognized and handled (or already handled this load).
  // The CDN also writes the code into the plain-text body of error responses
  // ("error 971"); responseBody is the fallback for when CORS hides the
  // header (Access-Control-Expose-Headers missing on the error response).
  const handleSegmentErrorCode = useCallback((errorCode: unknown, responseBody?: unknown): boolean => {
    let code = errorCode === undefined || errorCode === null ? '' : String(errorCode).trim();
    if (!code && typeof responseBody === 'string') {
      code = /^error\s+(\d+)$/i.exec(responseBody.trim())?.[1] ?? '';
    }
    if (!code) return false;
    if (code !== '940' && !SEGMENT_ERROR_MESSAGE_KEYS[code]) return false;
    if (segmentErrorHandledRef.current) return true;
    segmentErrorHandledRef.current = true;

    if (code === '940') {
      // Playback token expired (e.g. paused for too long) — a reload re-issues it.
      console.warn('Segment response returned X-Error-Code 940 (token expired), reloading page...');
      window.location.reload();
      return true;
    }

    console.warn(`Segment response returned X-Error-Code ${code}, blocking playback`);
    try {
      videoElementRef.current?.pause();
    } catch {}
    onPlaybackBlocked?.(SEGMENT_ERROR_MESSAGE_KEYS[code]);
    return true;
  }, [onPlaybackBlocked]);

  // Checks a Shaka BAD_HTTP_STATUS (1001) error for a segment response
  // carrying a blocking X-Error-Code. Non-2xx segment responses never reach
  // response filters, so the code arrives via the error's headers (data[3])
  // or response body (data[2]). Reaches us through Shaka's 'error' event
  // mid-playback, or as a load() rejection when the init segment fails.
  const handleSegmentHttpError = useCallback((error: any): boolean => {
    if (error?.code !== 1001 || !Array.isArray(error?.data)) return false;
    if (error.data[4] !== (shaka as any).net.NetworkingEngine.RequestType.SEGMENT) return false;
    return handleSegmentErrorCode(error.data[3]?.['x-error-code'], error.data[2]);
  }, [handleSegmentErrorCode]);

  // Internal function to handle the actual player initialization logic
  const initializePlayerInternal = useCallback(async (video: HTMLVideoElement) => {
    try {
      // ============================================================
      // LIFECYCLE COORDINATION & PLAYER CREATION
      // ============================================================
      // Serialize with any pending destroy to avoid MediaSource races
      if (destroyInProgressRef.current) {
        try {
          await destroyInProgressRef.current;
        } catch {
          // Ignore destroy errors here; we'll proceed with a clean init
        }
      }

      const myInitId = ++initSequenceRef.current;
      activeInitIdRef.current = myInitId;

      // Store video element reference for retry functionality
      videoElementRef.current = video;
      // Install polyfills
      shaka.polyfill.installAll();

      // Check for browser support
      if (!shaka.Player.isBrowserSupported()) {
        throw new Error('Browser not supported by Shaka Player');
      }
      
      // If a destroy was requested meanwhile, abort early
      if (isDestroyingRef.current) {
        return;
      }

      // Create player instance first, then attach the video element.
      const player = new (shaka.Player as any)();
      playerRef.current = player;

      // Attach to the video element (recommended approach since Shaka v4).
      await (player as any).attach(video);

      // ============================================================
      // PLAYER CONFIGURATION (DVR, Streaming Settings)
      // ============================================================
      // Configure default settings for expanding DVR window in live streams
      // This ensures the DVR window grows from the origin rather than sliding forward
      const defaultConfig = {
        manifest: {
          // Override availability window to allow DVR window to grow from start
          // Set to a very large value (24 hours in seconds) to effectively allow unlimited growth
          availabilityWindowOverride: 86400, // 24 hours in seconds
        },
        streaming: {
          // Allow seeking to any point within the availability window
          safeSeekOffset: 5, // 5 seconds from live edge to prevent buffering
          // Increase tolerance for manifest timing inaccuracies in live streams
          inaccurateManifestTolerance: 2,
        }
      };

      // Apply default config first
      player.configure(defaultConfig);

      // Configure player with user-provided config (can override defaults)
      if (shakaConfig) {
        player.configure(shakaConfig);
      }
      
      const playlistSrc = normalizeSource(src);
      const manifestUrl = getManifestUrl();
      let playlistId = playlistSrc.id;
      const isDRM = Boolean(playlistSrc.drm?.widevine || playlistSrc.drm?.fairplay || playlistSrc.drm?.playready);

      // Reset per-load guards
      storedPersistentThisLoadRef.current = false;
      segmentErrorHandledRef.current = false;

      // If init was superseded, tear down the created player and exit
      if (activeInitIdRef.current !== myInitId || isDestroyingRef.current) {
        try { await player.destroy(); } catch {}
        if (playerRef.current === player) playerRef.current = null;
        return;
      }

      // ============================================================
      // DRM CONFIGURATION
      // ============================================================
      
      // Handle persistent licenses for DRM content
      let storedSessionsMetadata: PersistentSessionMetadata[] = [];
      if (isDRM && playlistId) {
        storedSessionsMetadata = retrievePersistentLicense(playlistId, playlistSrc.drm?.licenseCacheKey ?? "");
      }

      if (isDRM) {
        // --- DRM Server & Key System Configuration ---
        const drmConfig: any = {
          servers: {
            'com.widevine.alpha': playlistSrc.drm?.widevine?.licenseUrl,
            'com.microsoft.playready': playlistSrc.drm?.playready?.licenseUrl,
            'com.apple.fps': playlistSrc.drm?.fairplay?.licenseUrl,
          },
          keySystemsMapping: {
            // Fall back or reroute to the platform's recommended PlayReady CDM if needed
            'com.microsoft.playready': 'com.microsoft.playready.recommendation'
          },
          ...(playlistSrc.drm?.fairplay && {
            advanced: {
              'com.apple.fps': {
                serverCertificateUri: playlistSrc.drm.fairplay.certificateUrl,
              }
            }
          })
        };

        drmConfig.advanced = {
          ...drmConfig.advanced,
          'com.widevine.alpha': {
            ...drmConfig.advanced?.['com.widevine.alpha'],
            sessionType: supportsWidevinePersistentLicenses() ? 'persistent-license' : 'temporary'
          },
          'com.microsoft.playready': {
            ...drmConfig.advanced?.['com.microsoft.playready'],
            sessionType: 'persistent-license' // PlayReady seems to always require persistent-license (temporary won't work, not compatible with license server response)
          },
          'com.apple.fps': {
            ...drmConfig.advanced?.['com.apple.fps'],
            sessionType: 'temporary' // FairPlay always uses temporary sessions - Safari won't play with persistent-license
          }
        };

        // Configure persistent session settings if we have stored sessions
        if (storedSessionsMetadata.length > 0) {
          drmConfig.persistentSessionOnlinePlayback = true;
          drmConfig.persistentSessionsMetadata = storedSessionsMetadata.map(session => ({
            sessionId: session.sessionId,
            initData: new Uint8Array(session.initData),
            initDataType: session.initDataType
          }));
        }

        usingPersistentLicenseRef.current = storedSessionsMetadata.length > 0;

        player.configure({ drm: drmConfig });

        // --- Stale License Recovery: 'waitingforkey' Handler ---
        // Detect situations where the browser is waiting for a decryption key but none becomes usable
        // This can happen when a persisted license is invalid/stale. We listen for 'waitingforkey'
        // and, if playback does not resume shortly, clear cached licenses and reload.
        const clearWaitingForKeyTimer = () => {
          if (waitingForKeyTimerRef.current !== null) {
            window.clearTimeout(waitingForKeyTimerRef.current);
            waitingForKeyTimerRef.current = null;
          }
        };

        const onPlaybackResumed = () => {
          clearWaitingForKeyTimer();
        };

        const onWaitingForKey = () => {
          // Only attempt recovery when we intentionally tried to use a cached persistent license
          if (!usingPersistentLicenseRef.current) return;
          // Avoid stacking multiple timers or looping retries
          if (isRetrying) return;
          clearWaitingForKeyTimer();

          // Give the player a short window to provide/refresh keys normally
          waitingForKeyTimerRef.current = window.setTimeout(async () => {
            try {
              if (isRetrying) return;

              // If we are still stuck without keys, clear cached persistent licenses and retry
              console.warn('Stuck waiting for decryption key; attempting license cache reset and reload...');
              const clearedCount = clearAllPersistentLicenses();

              if (clearedCount > 0 && playerRef.current) {
                setIsRetrying(true);
                await playerRef.current.load(getManifestUrl());
                console.log('Reloaded manifest after clearing cached licenses');
              } else {
                console.warn('No cached licenses found to clear, skipping reload');
              }
            } catch (e) {
              console.error('Failed during recovery from waitingforkey state:', e);
              onError?.(e as Error);
            } finally {
              setIsRetrying(false);
              clearWaitingForKeyTimer();
            }
          }, 3000);
        };

        // Attach listeners to the video element
        try {
          (video as any).addEventListener('waitingforkey', onWaitingForKey as any);
          video.addEventListener('playing', onPlaybackResumed);
          video.addEventListener('ended', onPlaybackResumed);
          video.addEventListener('pause', onPlaybackResumed);
          waitingForKeyHandlerRef.current = onWaitingForKey;
          playbackResumedHandlerRef.current = onPlaybackResumed;
        } catch (e) {
          console.warn('Failed to attach waitingforkey/playback listeners:', e);
        }

        // --- Network Engine Filters: Auth Headers & FairPlay Decoding ---
        const netEngine = player.getNetworkingEngine();
        if (netEngine) {
          netEngine.registerRequestFilter((type: any, request: any) => {
            switch (type) {
              case (shaka as any).net.NetworkingEngine.RequestType.LICENSE:
                if (publicKey) {
                  request.headers["authorization"] = `Bearer ${publicKey}`;
                }
                if (mottoToken) {
                  request.headers["x-motto-token"] = mottoToken;
                }
                break;
              case (shaka as any).net.NetworkingEngine.RequestType.MANIFEST:
              case (shaka as any).net.NetworkingEngine.RequestType.SEGMENT:
                // Allow cross-site credentials so that cookies can be set from the manifest to the segments for JWT validation
                request.allowCrossSiteCredentials = true
                break;
            }
          });

          // Register response filter for FairPlay base64 decoding
          netEngine.registerResponseFilter((type: any, response: any) => {
            if (type === (shaka as any).net.NetworkingEngine.RequestType.LICENSE) {
              const ks = player.keySystem && player.keySystem();
              if (ks === 'com.apple.fps') {
                const responseText = (shaka as any).util.StringUtils.fromUTF8(response.data);
                response.data = (shaka as any).util.Uint8ArrayUtils.fromBase64(responseText).buffer;
              }
            }
          });
        }
      }
      // ============================================================
      // END DRM CONFIGURATION
      // ============================================================

      // --- Segment X-Error-Code Handling (DRM & Non-DRM) ---
      // The CDN reports playback-blocking conditions (expired token, IP
      // change, VPN, anonymous proxy) via the X-Error-Code header on segment
      // responses. Response filters only run for 2xx responses; segments that
      // fail with a non-2xx status carry the same header through the
      // BAD_HTTP_STATUS branch of the 'error' listener below.
      const segmentNetEngine = player.getNetworkingEngine();
      if (segmentNetEngine) {
        segmentNetEngine.registerResponseFilter((type: any, response: any) => {
          if (type === (shaka as any).net.NetworkingEngine.RequestType.SEGMENT) {
            handleSegmentErrorCode(response?.headers?.['x-error-code']);
          }
        });
      }

      // --- API Token Authorization (DRM & Non-DRM) ---
      if (apiToken) {
        const netEngine = player.getNetworkingEngine();
        if (netEngine) {
          netEngine.registerRequestFilter((type: any, request: any) => {
            if (type === (shaka as any).net.NetworkingEngine.RequestType.MANIFEST) {
              request.headers["authorization"] = `Bearer ${apiToken}`;
            }
          });
        }
      }

      // --- License Persistence: Cache Valid Licenses ---
      // Persist licenses after Shaka signals license expiration updates, which occur
      // only after successful license acquisition/refresh. This avoids caching on errors
      // or during the initial clear (unencrypted) segment playback.
      if (isDRM && playlistId) {
        const onDRMSessionUpdate = () => {
          try {
            if (storedPersistentThisLoadRef.current) return;
            const activeDrmSessions = player.getActiveSessionsMetadata?.();
            if (!activeDrmSessions) return;

            const persistentSessions = activeDrmSessions.filter(
              (session: any) => session.sessionType === 'persistent-license'
            );

            if (persistentSessions.length > 0) {
              const sessionsToStore: PersistentSessionMetadata[] = persistentSessions.map((session: any) => ({
                sessionId: session.sessionId,
                initData: session.initData,
                initDataType: session.initDataType,
                keySystem: session.keySystem || 'com.widevine.alpha'
              }));
              storePersistentLicense(playlistId, playlistSrc.drm?.licenseCacheKey ?? "", sessionsToStore);
              storedPersistentThisLoadRef.current = true;
            }
          } catch (e) {
            console.warn('Failed to persist licenses on expiration update:', e);
          }
        };

        try {
          player.addEventListener('drmsessionupdate', onDRMSessionUpdate as any);
          drmExpirationHandlerRef.current = onDRMSessionUpdate as any;
        } catch (e) {
          console.warn('Failed to attach drmsessionupdate listener:', e);
        }
      }
      
      // ============================================================
      // ERROR HANDLING & DRM RETRY LOGIC
      // ============================================================
      player?.addEventListener('error', (event: any) => {
        const error = event.detail;

        // Ignore Shaka's LOAD_INTERRUPTED (code 7000) since it indicates a
        // benign race between load/unload calls when the source changes.
        if (error?.code === 7000) {
          return;
        }

        // Segment responses with a non-2xx status never reach response
        // filters; Shaka raises BAD_HTTP_STATUS (code 1001) instead. Check it
        // for a blocking X-Error-Code.
        if (handleSegmentHttpError(error)) {
          return;
        }

        // Check if error code is in the 6000-6999 range (DRM-related errors)
        // and trigger license clearing and retry
        if (error?.code >= 6000 && error?.code < 7000 && !isRetrying && videoElementRef.current) {
          console.warn(`DRM error detected (code: ${error.code}), checking for cached licenses...`);
          
          // Clear all cached licenses and check if any were actually cleared
          const clearedCount = clearAllPersistentLicenses();
          
          // Only retry if we actually cleared some licenses
          if (clearedCount > 0) {
            console.warn(`Cleared ${clearedCount} cached licenses, retrying...`);
            setIsRetrying(true);
            
            // Use setTimeout to avoid blocking and allow the error to be processed
            setTimeout(async () => {
              try {
                // Get the video element and current manifest URL
                const video = videoElementRef.current;
                const currentPlayer = playerRef.current;
                
                if (video && currentPlayer) {
                  console.log('Reloading manifest after license cache clear...');
                  
                  // Simply reload the manifest with the current player instance
                  // This will trigger new license requests without the cached licenses
                  await currentPlayer.load(getManifestUrl());
                  
                  console.log('Manifest reloaded successfully');
                }
              } catch (retryError) {
                console.error('Failed to retry after license clear:', retryError);
                onError?.(retryError as Error);
                onFatalError?.();
              } finally {
                setIsRetrying(false);
              }
            }, 500); // Give a bit more time for the error to be processed
            return;
          } else {
            console.warn(`No cached licenses found to clear for error code ${error.code}, not retrying to avoid infinite loop`);
          }
        }

        console.error('Shaka Player Error:', error);
        onError?.(new Error(`Shaka Player Error: ${error.message || 'Unknown error'}`));

        // Recoverable errors (severity 1) are ones Shaka keeps retrying
        // (e.g. a temporarily disabled stream); only give up on critical ones.
        if (error?.severity === (shaka as any).util.Error.Severity.CRITICAL) {
          onFatalError?.();
        }
      });

      // ============================================================
      // MUX ANALYTICS INITIALIZATION
      // ============================================================
      // Initialize Mux BEFORE loading so it can hook into networking engine
      if (muxConfig) {
        try {
          const playerInitTime = initShakaPlayerMux.utils.now();
          
          const muxOptions = {
            debug: muxConfig.debug || false,
            disableCookies: muxConfig.disableCookies || false,
            respectDoNotTrack: muxConfig.respectDoNotTrack || false,
            automaticErrorTracking: muxConfig.automaticErrorTracking !== false,
            ...(muxConfig.beaconCollectionDomain && { beaconCollectionDomain: muxConfig.beaconCollectionDomain }),
            ...(muxConfig.errorTranslator && { errorTranslator: muxConfig.errorTranslator }),
            data: {
              env_key: muxConfig.envKey,
              player_name: muxConfig?.metadata?.player_name,
              player_version: version,
              player_init_time: playerInitTime,
              video_title: muxConfig?.metadata?.video_title ?? "",
              video_id: muxConfig?.metadata?.video_id ?? "",
              viewer_user_id: muxConfig?.metadata?.viewer_user_id ?? "",
              ...muxConfig.metadata,
            }
          };

          // Give Mux a token-scrubbing view of the player so signed CDN tokens
          // never reach analytics, while the real player keeps full signed URLs
          // for playback (manifest refreshes, segment fetches).
          muxMonitorRef.current = initShakaPlayerMux(createMuxScrubbingPlayer(player), muxOptions, shaka);
          onMuxReady?.();
        } catch (error) {
          console.error('Failed to initialize Mux Analytics:', error);
        }
      }
      
      // ============================================================
      // MANIFEST LOADING (Conditional)
      // ============================================================
      // Final guard before load to avoid racing with teardown
      if (activeInitIdRef.current !== myInitId || isDestroyingRef.current) {
        try { await player.destroy(); } catch {}
        if (playerRef.current === player) playerRef.current = null;
        return;
      }
      
      // IMPORTANT: Skip automatic manifest loading if System73 or ads are configured
      // 
      // Why skip for System73?
      // - System73 SDK must wrap the player BEFORE manifest loads
      // - This allows it to intercept manifest parsing and detect all ABR profiles
      // - If we load now, System73 would only see the single bitrate Shaka selects
      // - Result: streams incorrectly report as mono-bitrate in analytics
      //
      // Why skip for ads?
      // - Google IMA needs to intercept media loading to serve pre-roll ads
      // - The IMA loader will call loadManifest() after ads complete
      //
      // When skipped: usePlayerInitialization will call loadManifest() explicitly
      // after System73/ads are ready
      if (!hasAds && !hasSystem73) {
        // console.log('📺 [Shaka] Auto-loading manifest:', manifestUrl);
        await player.load(manifestUrl);
        // console.log('✅ [Shaka] Manifest auto-loaded successfully');
      } else {
        // console.log('⏭️  [Shaka] Skipping auto-load (hasAds:', hasAds, ', hasSystem73:', hasSystem73, ')');
      }
      
      onPlayerReady?.(player);

      return player;
    } catch (error) {
      // Filter out benign load interruptions that occur when the component
      // switches source before the previous load completes.
      if ((error as any)?.code === 7000) {
        return;
      }

      // A blocked init segment rejects load() instead of firing the 'error'
      // event; the blocking screen is already up via onPlaybackBlocked.
      // Rethrow so the caller still reports the failed load to Mux.
      if (handleSegmentHttpError(error)) {
        throw error;
      }

      console.error('Error initializing Shaka Player:', error);
      onError?.(error as Error);
      onFatalError?.();
      throw error;
    }
  }, [shakaConfig, drmConfig, src, onError, onFatalError, onPlayerReady, muxConfig, onMuxReady, isRetrying, getManifestUrl, apiToken, handleSegmentErrorCode, handleSegmentHttpError]);

  // Public initializePlayer function that components will use
  const initializePlayer = useCallback(async (video: HTMLVideoElement) => {
    return initializePlayerInternal(video);
  }, [initializePlayerInternal]);

  // Report errors from load()/init to Mux. Playback errors after a successful
  // load reach Mux by itself via the player's 'error' event; load failures only
  // reach it through this handler.
  const handleMuxError = useCallback((error: unknown) => {
    try {
      muxMonitorRef.current?.loadErrorHandler?.(error);
    } catch (e) {
      console.warn('Failed to report load error to Mux:', e);
    }
  }, []);

  // Public method to load the manifest after ads are set up
  const loadManifest = useCallback(async () => {
    const player = playerRef.current;
    if (!player) {
      console.warn('⚠️  [Shaka] Cannot load manifest: player not initialized');
      return;
    }

    try {
      const manifestUrl = getManifestUrl();
      // console.log('📺 [Shaka] Loading manifest:', manifestUrl);
      await player.load(manifestUrl);
      // console.log('✅ [Shaka] Manifest loaded successfully');
    } catch (error) {
      // Filter out benign load interruptions
      if ((error as any)?.code === 7000) {
        return;
      }
      // See the matching check in initializePlayerInternal's catch.
      if (handleSegmentHttpError(error)) {
        throw error;
      }
      console.error('❌ [Shaka] Error loading manifest:', error);
      onError?.(error as Error);
      onFatalError?.();
      throw error;
    }
  }, [getManifestUrl, onError, onFatalError, handleSegmentHttpError]);

  const destroyPlayer = useCallback(async () => {
    const playerInstance = playerRef.current;
    if (playerInstance) {
      try {
        isDestroyingRef.current = true;
        activeInitIdRef.current = null;
        // Clean up video element listeners and timers
        if (videoElementRef.current) {
          try {
            if (waitingForKeyHandlerRef.current) {
              (videoElementRef.current as any).removeEventListener('waitingforkey', waitingForKeyHandlerRef.current as any);
            }
            if (playbackResumedHandlerRef.current) {
              videoElementRef.current.removeEventListener('playing', playbackResumedHandlerRef.current);
              videoElementRef.current.removeEventListener('ended', playbackResumedHandlerRef.current);
              videoElementRef.current.removeEventListener('pause', playbackResumedHandlerRef.current);
            }
          } catch (e) {
            console.warn('Error removing media event listeners:', e);
          }
        }
        if (waitingForKeyTimerRef.current !== null) {
          window.clearTimeout(waitingForKeyTimerRef.current);
          waitingForKeyTimerRef.current = null;
        }

        // Clean up DRM-related player listeners
        try {
          if (drmExpirationHandlerRef.current && playerInstance.removeEventListener) {
            playerInstance.removeEventListener('drmsessionupdate', drmExpirationHandlerRef.current as any);
          }
        } catch (e) {
          console.warn('Error removing DRM expiration listener:', e);
        } finally {
          drmExpirationHandlerRef.current = null;
        }
        // Ensure only one destroy runs and let init await it
        const performDestroy = async () => {
          try {
            await playerInstance.destroy();
          } finally {
            // After Shaka teardown, also clear the HTMLVideoElement src to drop any object URL
            try {
              if (videoElementRef.current) {
                videoElementRef.current.removeAttribute('src');
                // Calling load() ensures the element releases previous MediaSource eagerly
                videoElementRef.current.load();
              }
            } catch {}
          }
        };
        destroyInProgressRef.current = performDestroy();
        await destroyInProgressRef.current;
      } catch (error) {
        console.warn('Error destroying Shaka Player:', error);
      } finally {
        if (playerRef.current === playerInstance) {
          playerRef.current = null;
        }
        muxMonitorRef.current = null;
        storedPersistentThisLoadRef.current = false;
        isDestroyingRef.current = false;
        destroyInProgressRef.current = null;
      }
    }
  }, [playerRef]);

  return {
    playerRef,
    initializePlayer,
    loadManifest,
    destroyPlayer,
    handleMuxError,
    isRetrying
  };
};